import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";

const API_URL = API_ENDPOINTS.PAYMENT_TERMS;

const EditPaymentTermForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paymentTerm, setPaymentTerm] = useState({
    name: "",
    publish: false,
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchPaymentTerm();
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchPaymentTerm = async () => {
    try {
      const response = await axios.get(API_URL.GET(id));
      setPaymentTerm({
        name: response.data.paymentTerm.name || "",
        publish: response.data.paymentTerm.publish || false,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching payment term:", error);
      setSnackbar({
        open: true,
        message: "Failed to load payment term data",
        severity: "error",
      });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setPaymentTerm({ ...paymentTerm, [e.target.name]: e.target.value });
  };

  const handlePublishToggle = () => {
    setPaymentTerm((prev) => ({ ...prev, publish: !prev.publish }));
  };

  const handleUpdate = async () => {
    try {
      await axios.put(API_URL.EDIT(id), paymentTerm);
      setSnackbar({
        open: true,
        message: "Payment term updated successfully!",
        severity: "success",
      });
      // Navigate after a short delay to allow the success message to be seen
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error("Error updating payment term:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update payment term. Please try again.",
        severity: "error",
      });
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />;
  }

  return (
    <Card sx={{ p: 4, maxWidth: 500, mx: "auto", mt: 5 }}>
      <Typography variant="h5" mb={3}>
        Edit Payment Term
      </Typography>
      <Box display="flex" flexDirection="column" gap={3}>
        <TextField
          label="Payment Term Name"
          name="name"
          value={paymentTerm.name}
          onChange={handleChange}
          required
        />
        <FormControlLabel
          control={
            <Switch
              checked={paymentTerm.publish}
              onChange={handlePublishToggle}
              color="primary"
            />
          }
          label="Published"
        />
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Button variant="contained" color="primary" onClick={handleUpdate}>
            Update
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EditPaymentTermForm;
